import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Plus, X, Pencil, Trash2, Loader2, Check, Package, Clock, TrendingUp } from "lucide-react";

interface SpaPackage { id: number; name: string; description?: string; price: number; duration: number; service_ids: number[]; available: boolean; }
interface SpaService { id: number; name: string; duration: number; price: number; }
function authH() { return { Authorization: `Bearer ${localStorage.getItem("ai2_token")}`, "x-hotel": localStorage.getItem("ai2_hotel") || "" }; }

export default function SpaPackages({ API, services, isSpaManager }: { API: string; services: SpaService[]; isSpaManager: boolean }) {
  const [pkgs, setPkgs]     = useState<SpaPackage[]>([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal]   = useState(false);
  const [edit, setEdit]     = useState<Partial<SpaPackage> | null>(null);
  const [saving, setSaving] = useState(false);

  const fetch = useCallback(async () => {
    setLoading(true);
    try { const { data } = await axios.get(`${API}/packages`, { headers: authH() }); setPkgs(data); }
    catch { /**/ } finally { setLoading(false); }
  }, [API]);

  useEffect(() => { fetch(); }, [fetch]);

  const save = async () => {
    if (!edit?.name?.trim()) return;
    setSaving(true);
    try {
      if (edit.id) await axios.put(`${API}/packages/${edit.id}`, edit, { headers: authH() });
      else          await axios.post(`${API}/packages`, edit, { headers: authH() });
      setModal(false); setEdit(null); fetch();
    } catch { /**/ } finally { setSaving(false); }
  };

  const del = async (id: number) => {
    if (!confirm("Delete this package?")) return;
    await axios.delete(`${API}/packages/${id}`, { headers: authH() });
    setPkgs(p => p.filter(x => x.id !== id));
  };

  const toggleSvc = (id: number) => {
    setEdit(e => {
      if (!e) return e;
      const ids = e.service_ids || [];
      return { ...e, service_ids: ids.includes(id) ? ids.filter(x => x !== id) : [...ids, id] };
    });
  };

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 }}>
        <div>
          <div style={{ fontSize: 15, fontWeight: 700, color: "#F2EBD9" }}>Packages & Bundles</div>
          <div style={{ fontSize: 12, color: "rgba(242,235,217,0.4)", marginTop: 2 }}>{pkgs.filter(p => p.available).length} active</div>
        </div>
        {isSpaManager && <button className="spa-btn-primary" onClick={() => { setEdit({ name: "", description: "", price: 0, duration: 60, service_ids: [], available: true }); setModal(true); }}><Plus size={14} /> New Package</button>}
      </div>

      {modal && edit && (
        <div className="spa-modal-overlay" onClick={() => setModal(false)}>
          <div className="spa-modal" onClick={e => e.stopPropagation()}>
            <div className="spa-modal-header">
              <h2 className="spa-modal-title">{edit.id ? "Edit Package" : "New Package"}</h2>
              <button className="spa-modal-close" onClick={() => setModal(false)}><X size={16} /></button>
            </div>
            <div className="spa-modal-body">
              <div className="spa-form-group"><label>Package Name</label><input value={edit.name || ""} onChange={e => setEdit(s => ({ ...s, name: e.target.value }))} placeholder="e.g. Couples Retreat, Detox Day" /></div>
              <div className="spa-form-row">
                <div className="spa-form-group" style={{ marginBottom: 0 }}><label>Price ($)</label><input type="number" min={0} value={edit.price ?? 0} onChange={e => setEdit(s => ({ ...s, price: Number(e.target.value) }))} /></div>
                <div className="spa-form-group" style={{ marginBottom: 0 }}><label>Total Duration (min)</label><input type="number" min={15} step={15} value={edit.duration ?? 60} onChange={e => setEdit(s => ({ ...s, duration: Number(e.target.value) }))} /></div>
              </div>
              <div className="spa-form-group" style={{ marginTop: 12 }}><label>Description</label><textarea rows={2} value={edit.description || ""} onChange={e => setEdit(s => ({ ...s, description: e.target.value }))} placeholder="What's included…" /></div>
              <div className="spa-form-group" style={{ marginTop: 12 }}>
                <label>Included Services</label>
                <div style={{ display: "flex", flexDirection: "column", gap: 6, maxHeight: 200, overflowY: "auto" }}>
                  {services.map(s => (
                    <label key={s.id} style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", padding: "6px 10px", borderRadius: 8, background: (edit.service_ids || []).includes(s.id) ? "rgba(201,168,76,0.1)" : "rgba(255,255,255,0.02)", border: `1px solid ${(edit.service_ids || []).includes(s.id) ? "rgba(201,168,76,0.35)" : "rgba(255,255,255,0.05)"}` }}>
                      <input type="checkbox" checked={(edit.service_ids || []).includes(s.id)} onChange={() => toggleSvc(s.id)} style={{ accentColor: "#c9a84c" }} />
                      <span style={{ flex: 1, fontSize: 13, color: "rgba(242,235,217,0.8)" }}>{s.name}</span>
                      <span style={{ fontSize: 11, color: "rgba(242,235,217,0.4)" }}>{s.duration}min · ${s.price}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div className="spa-form-group" style={{ marginTop: 12 }}>
                <label>Available</label>
                <label className="spa-checkbox-row"><input type="checkbox" checked={edit.available ?? true} onChange={e => setEdit(s => ({ ...s, available: e.target.checked }))} /> Active & bookable</label>
              </div>
            </div>
            <div className="spa-modal-footer">
              <button className="spa-btn-ghost" onClick={() => setModal(false)}>Cancel</button>
              <button className="spa-btn-primary" onClick={save} disabled={saving || !edit.name?.trim()}>
                {saving ? <Loader2 size={14} className="spa-spinner" /> : <Check size={14} />}{edit.id ? "Save" : "Create Package"}
              </button>
            </div>
          </div>
        </div>
      )}

      {loading ? <div className="spa-loading"><Loader2 size={18} className="spa-spinner" /> Loading…</div> : pkgs.length === 0 ? (
        <div className="spa-fully-booked"><Package size={28} style={{ marginBottom: 8, opacity: 0.3 }} /><br />No packages yet</div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 14 }}>
          {pkgs.map(p => {
            const included = services.filter(s => (p.service_ids || []).includes(s.id));
            return (
              <div key={p.id} style={{ background: "#0E1E38", border: `1px solid ${p.available ? "rgba(201,168,76,0.2)" : "rgba(255,255,255,0.05)"}`, borderRadius: 14, padding: "18px 20px", opacity: p.available ? 1 : 0.5 }}>
                <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 10 }}>
                  <div>
                    <div style={{ fontWeight: 800, color: "#F2EBD9", fontSize: 15 }}>{p.name}</div>
                    {p.description && <div style={{ fontSize: 12, color: "rgba(242,235,217,0.45)", marginTop: 3 }}>{p.description}</div>}
                  </div>
                  {isSpaManager && (
                    <div style={{ display: "flex", gap: 5 }}>
                      <button className="spa-action-btn" style={{ padding: "3px 8px" }} onClick={() => { setEdit({ ...p }); setModal(true); }}><Pencil size={11} /></button>
                      <button className="spa-action-btn danger" style={{ padding: "3px 8px" }} onClick={() => del(p.id)}><Trash2 size={11} /></button>
                    </div>
                  )}
                </div>
                <div style={{ display: "flex", gap: 14, marginBottom: 12 }}>
                  <span style={{ fontSize: 18, fontWeight: 800, color: "#c9a84c" }}>${p.price}</span>
                  <span style={{ fontSize: 12, color: "rgba(242,235,217,0.4)", alignSelf: "center" }}><Clock size={11} style={{ display: "inline", marginRight: 3 }} />{p.duration} min</span>
                </div>
                {included.length > 0 && (
                  <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                    {included.map(s => (
                      <div key={s.id} style={{ fontSize: 12, color: "rgba(242,235,217,0.5)", display: "flex", alignItems: "center", gap: 6 }}>
                        <Check size={10} color="#34d399" /> {s.name}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
